import { useState } from 'react';
import { noop } from 'lodash';
import type {
  Collection,
  DeleteCollectionMutation,
} from '@frameio/graphql-client/generated';
import { useDeleteCollectionMutation } from '@frameio/graphql-client/generated';
import { DeleteConfirmation } from '@frameio/shared/resources';
import { useTrack } from '@frameio/shared/segment/hooks';
import { Box, Text, ToastAlert, useToast } from '@frameio/vapor';
import { removeCollectionFromCache } from '../cache-ops/removeCollectionFromCache';
import TEXT from './generated/TEXT/useDeleteCollection';

export type DeletableCollection = Pick<Collection, 'id' | 'name'>;

export type DeleteCollectionOpts = {
  projectId: string;
  onSuccess: (result: DeleteCollectionMutation) => void;
};

type DeleteCollectionReturn = {
  deleteDialog: React.ReactNode;
  requestDelete: (collection: DeletableCollection) => void;
};

const DEFAULT_OPTS: Partial<DeleteCollectionOpts> = {
  onSuccess: noop as (result: DeleteCollectionMutation) => void,
};

/**
 * The useDeleteCollection hook can be used to delete a collection.
 *
 * @param opts.projectId the id of the project to delete a collection from
 * @param opts.onSuccess a function to call when the collection is successfully deleted
 * @returns a dialog component to confirm the deletion and a function to call when
 * the user confirms.
 */
export function useDeleteCollection(
  opts?: DeleteCollectionOpts
): DeleteCollectionReturn {
  const { projectId, onSuccess } = {
    ...DEFAULT_OPTS,
    ...opts,
  };
  const { addToast } = useToast();
  const [collectionToDelete, setCollectionToDelete] =
    useState<DeletableCollection | null>(null);
  const [isModalShowing, setModalShowing] = useState(false);
  const [deleteCollection] = useDeleteCollectionMutation();
  const track = useTrack();

  const handleError = () => {
    addToast(
      (closeToast, toastCloseLabel) => (
        <ToastAlert
          closeLabel={toastCloseLabel}
          onRequestClose={closeToast}
          title={TEXT.deleteToastError(collectionToDelete) as string}
          variant="error"
        />
      ),
      { duration: 2000 }
    );
  };

  const handleDeleteClick = () => {
    if (!collectionToDelete) return;

    track(
      'collection-deleted',
      { collection_id: collectionToDelete.id },
      { logToDatadog: true }
    );
    setModalShowing(false);
    deleteCollection({
      refetchQueries: [
        'GetCollectionsForProject',
        'GetSharesForProjectNavigation',
        'GetSharesForAllSharesTable',
      ],
      variables: {
        input: {
          collectionId: collectionToDelete.id,
        },
      },
      optimisticResponse: {
        deleteCollection: {
          collection: {
            id: collectionToDelete.id,
            __typename: 'Collection',
          },
          __typename: 'DeleteCollectionPayload',
        },
      },
      update: (cache, { data }) => {
        const collectionId = data?.deleteCollection?.collection?.id;

        if (!collectionId) return;

        removeCollectionFromCache(cache, collectionId, projectId as string);
      },
      onCompleted: (data) => {
        if (data?.deleteCollection?.collection) {
          addToast(
            (closeToast, toastCloseLabel) => (
              <ToastAlert
                closeLabel={toastCloseLabel}
                onRequestClose={closeToast}
                title={TEXT.deleteToast(collectionToDelete) as string}
                variant="success"
              />
            ),
            { duration: 2000 }
          );

          onSuccess?.(data);
        } else {
          handleError();
        }

        setCollectionToDelete(null);
      },
      onError: () => {
        handleError();
      },
    });
  };

  const deleteDialog = (
    <DeleteConfirmation
      confirmButtonText={TEXT.deleteConfirmationButton}
      isShowing={isModalShowing}
      message={TEXT.deleteConfirmation(
        collectionToDelete as DeletableCollection
      )}
      onConfirm={handleDeleteClick}
      onRequestClose={() => {
        setModalShowing(false);
        setCollectionToDelete(null);
      }}
      requireExtraConfirmation={false}
      title={TEXT.deleteTitle}
    />
  );

  return {
    deleteDialog,
    requestDelete(collection: DeletableCollection) {
      setModalShowing(true);
      setCollectionToDelete(collection);
    },
  };
}

TEXT.DEFINE(
  {
    /** Confirmation dialog body text for deleting a collection */
    deleteConfirmation: (c?: { name: string }) => {
      const name = c?.name ?? '';
      return (
        <>
          <Box paddingBottom={24}>
            <Text color="tertiary">
              Are you sure you want to delete{' '}
              {`'${(
                <Text color="primary" display="inline">
                  {name}
                </Text>
              )}'?`}
              No assets will be deleted and you can still access them in their
              respective folders.
            </Text>
          </Box>
          <Text color="tertiary">
            Any Shares associated with this Collection will also be deleted.
          </Text>
        </>
      );
    },
    /** Confirmation dialog action label */
    deleteConfirmationButton: 'Delete',
    /** Confirmation dialog title */
    deleteTitle: 'Delete Collection',
    /** Toast title shown when deletion succeeds */
    deleteToast: (c?: { name: string }) => {
      const name = c?.name ?? '';
      return `Successfully deleted collection '${name}'`;
    },
    /** Toast title shown when deletion fails */
    deleteToastError: (c?: { name: string }) => {
      const name = c?.name ?? '';
      return `Failed to delete collection '${name}'`;
    },
  },
  { Box, Text }
);
